import { useMutation } from '@tanstack/react-query';
import { useHistory } from 'react-router-dom';
import { story, getAIStory } from 'admin/resources';
import { selectActiveOrganizationId } from 'admin/reducers/user/selectors';
import { useAdminSelector } from 'admin/reducers';
import { pollWithTimeout } from 'common/utils/polling';
import { generatePath } from 'common/utils/route';
import { STORY_FLOW_PAGE } from 'admin/constants/routes';
import t from 'common/utils/translate';
import type { SupportedLanguage } from 'common/i18n/types';
import type { TranslationResponse, TranslationStatusResponse, TranslationJob } from './types';

const statusMessages: Record<TranslationJob['status'], string> = {
	starting: t('translationModal.status.starting'),
	generating: t('translationModal.status.translating'),
	completed: t('translationModal.status.completed'),
	failed: t('translationModal.status.failed'),
};

export function useStoryTranslation() {
	const history = useHistory();
	const organizationId = useAdminSelector(selectActiveOrganizationId);

	const translateMutation = useMutation({
		mutationFn: async ({ storyId, language }: { storyId: string; language: SupportedLanguage }) => {
			const response = await story.translate
				.params({ storyId })
				.send<{ body: TranslationResponse }>({ language });

			return response.body;
		},
	});

	const translateStory = async (
		storyId: string,
		language: SupportedLanguage,
		onStatusChange?: (status: string) => void
	) => {
		if (!organizationId) {
			throw new Error('No organization selected');
		}

		// Initiate translation
		const translationResponse = await translateMutation.mutateAsync({ storyId, language });

		// Poll for completion
		const pollResult = await pollWithTimeout({
			checkStatus: async () => {
				const response = await getAIStory
					.params({ organizationId, generatorId: translationResponse.id })
					.send<{ body: TranslationStatusResponse }>({ jobId: translationResponse.jobId });

				return response.body;
			},
			shouldContinue: data => {
				const job = data.jobs.find(j => j.id === translationResponse.jobId);
				return job?.status !== 'completed';
			},
			isFailed: data => {
				const job = data.jobs.find(j => j.id === translationResponse.jobId);
				return job?.status === 'failed';
			},
			getStatusMessage: data => {
				const job = data.jobs.find(j => j.id === translationResponse.jobId);
				const status = job?.status;
				const statuses = job?.statuses || [];

				// Use the last status message if available, otherwise map from status field
				if (statuses.length > 0) {
					return statuses[statuses.length - 1];
				}

				return (status && statusMessages[status]) || t('translationModal.status.translating');
			},
			interval: 5000,
			timeout: 300_000, // 5 minutes
			retries: 5, // Retry failed operations 5 times
			onStatusChange,
		});

		if (!pollResult.success || !pollResult.data) {
			throw new Error(pollResult.error || t('translationModal.status.failed'));
		}

		const job = pollResult.data.jobs.find(j => j.id === translationResponse.jobId);
		const translatedStoryId = job?.prompts[0]?.response?.storyId;

		if (!translatedStoryId) {
			throw new Error('Translation completed but no story ID found');
		}

		// Navigate to the new translated story
		history.push(generatePath(STORY_FLOW_PAGE, { storyId: translatedStoryId }));

		return translatedStoryId;
	};

	return {
		translateStory,
		isLoading: translateMutation.isPending,
		error: translateMutation.error,
	};
}
